import { storeToRefs } from 'pinia'
import {
  NavigationGuardReturn,
  RouteLocationNormalized,
  RouteLocationRaw,
  createRouter,
  createWebHistory,
} from 'vue-router'

import { ray } from 'node-ray/web'
import {
  type RouteNamedMap,
  handleHotUpdate,
  routes as routesAuto,
} from 'vue-router/auto-routes'

import { useNavigation } from './navigation/composables/useNavigation'
import { useHasPermission } from './permissions/composables/useHasPermission'
import { isManagerOnlyRoute } from './permissions/managerOnlyRoutes'

import { ToastEvent } from './common/models/toastEvent'
import { isTransientApiError } from './shared/api/apiError'
import { OnboardingDraft } from './shared/api/generated/models'

import { useEventStore } from './common/eventStore'
import { useFeatureFlagStore } from './features/featureFlagStore'
import { useAuthSiteStore } from './user/authSiteStore'
import { useAuthUserStore } from './user/authUserStore'

import { dashboardRoutes } from './dashboard/routes'
import { steps } from './onboarding/stepRegistry'

function toNamedRoute<T extends keyof RouteNamedMap>(
  name: T,
  params?: RouteNamedMap[T]['params'],
  query?: Record<string, string>,
): RouteLocationRaw {
  return { name, params, query } as RouteLocationRaw
}

/** Staff land on My Account, which points them to the CliniShift app (E2E-R4-01) */
const STAFF_LANDING = '/user'

function toUnavailable(to: RouteLocationNormalized): RouteLocationRaw {
  return toNamedRoute('/(guest)/unavailable', {}, { redirect: to.fullPath })
}

async function beforeEach(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): Promise<NavigationGuardReturn> {
  ray().separator()
  ray()
    .table({
      full_path: to.fullPath + ' -> ' + from.fullPath,
      path: to.path + ' -> ' + from.path,
      to_meta: to.meta,
      from_meta: from.meta,
    })
    .label('router')
    .color('purple')

  if (to.name === '/(guest)/forbidden' || to.name === '/(guest)/unavailable') {
    return true
  }

  const { beginNavigation } = useNavigation()
  beginNavigation()

  const authUserStore = useAuthUserStore()
  const { isAuthenticated, isInitialised, isUnavailable } =
    storeToRefs(authUserStore)
  const { fetchUser } = authUserStore

  // Always ensure auth has been initialised before allowing navigation to proceed
  if (!isInitialised.value) {
    ray('not init')
    await fetchUser()

    // The API is busy or down (429, 5xx, network): the user is unknown, not signed out (E2E-R4-02)
    if (isUnavailable.value) {
      return toUnavailable(to)
    }
  }

  const authenticated = isAuthenticated.value
  const isGuestRoute = !!to.meta.isGuest
  // We're using multiple layout files for different group route folder
  //  This creates multiple base paths ('/') which override the meta for each other
  //  We can just check the base path manually instead of a layout switching base layout
  //  or adding a path to our grouped routes.
  const requiresAuth = !!to.meta.requiresAuth || to.path === '/'
  const requiresOrganisation = !!to.meta.requiresOrganisation
  const permission = to.meta.permission
  const featureFlags = to.meta.featureFlags

  const authSiteStore = useAuthSiteStore()
  const { sites, lastSlug } = storeToRefs(authSiteStore)
  const { ensureSites, ensureSiteContext, findBySlug } = authSiteStore
  const featureFlagStore = useFeatureFlagStore()

  // Guest routes redirect authenticated users to the default site dashboard or business select if they have no sites
  if (isGuestRoute && authenticated) {
    ray('Trying Guest route while authenticated. Redirecting base path')
      .label('router')
      .color('orange')
    return { path: '/' }
  }

  // Authenticated routes redirect unauthenticated users to the login page
  if (requiresAuth && !authenticated) {
    ray(
      'Trying authenticated route while not authenticated. Redirecting to login',
    )
      .label('router')
      .color('orange')
    return toNamedRoute('/(guest)/login')
  }

  if (to.path === '/') {
    ray('Trying base path. Redirecting to default site dashboard')
      .label('router')
      .color('orange')
    await ensureSites()

    // Staff are sent on to My Account by the org check below (E2E-R4-01). It can't be decided here:
    // the org list's permissions are all false, only GET api/orgs/{id} has the user's real ones.
    const lastSite = lastSlug.value ? findBySlug(lastSlug.value) : null
    if (lastSite) {
      ray('last site')
      return dashboardRoutes.dashboard(lastSite.slug)
    }

    if (sites.value?.length === 1) {
      ray('default last from count')
      return dashboardRoutes.dashboard(sites.value[0].slug)
    }

    ray('to select')

    return toNamedRoute('/(guest)/org.select')
  }

  // Organisation routes require an authenticated user, so we can check for the site and permissions before allowing navigation
  if (requiresOrganisation && authenticated) {
    // TODO: This should never happen since all org routes require a slug
    const slug = (to.params as { org?: string }).org
    if (!slug) {
      ray('Organisation route missing org param. Redirecting to select site')
        .label('router')
        .color('red')
      return toNamedRoute('/(guest)/org.select')
    }

    await ensureSites()

    const site = findBySlug(slug)
    if (!site) {
      ray('didnt find site from slug')
      return toNamedRoute('/(guest)/org.select')
    }

    await ensureSiteContext(site)

    // const needsOnboardingRecord =
    //   to.meta.onboardingEntry || to.name === '/(app)/[org]/onboarding/[stepId]'
    // if (!site.hasCompletedOnboarding && needsOnboardingRecord) {
    //   // const queryClient = useQueryClient()
    //   // // const record = await queryClient.ensureQueryData({
    //   // //   queryKey: onboardingDraftKeys.all,
    //   // //   queryFn: async () => ((await loadDraft(site.id)) ?? { revision: 0, draft: emptyDraft(), completed_at: null as string | null}),
    //   // // })
    //   const record = await loadDraft(site.id)
    //   const resume = resolveResumeStep(record)
    //   console.log('resume', resume)
    //   if (to.meta.onboardingEntry) {
    //     return resume
    //       ? toNamedRoute('/(app)/[org]/onboarding/[stepId]', {
    //           org: site.slug,
    //           stepId: resume,
    //         })
    //       : toNamedRoute('/(app)/[org]/dashboard/', { org: site.slug })
    //   }
    // }
    // if (!site.hasCompletedOnboarding && !to.path.includes('onboarding')) {
    //   ray('redirecting to onboarding')
    //   return toNamedRoute('/(app)/[org]/onboarding/', { org: site.slug })
    // } else if (site.hasCompletedOnboarding && to.path.includes('onboarding')) {
    //   return toNamedRoute('/(app)/[org]/dashboard/', { org: site.slug })
    // }

    if (featureFlags && !featureFlagStore.isOn(featureFlags)) {
      ray('Missing feature flag, cancelling navigation')
        .label('router')
        .color('orange')
      ray().separator()
      
      return '/forbidden'
    }

    const { can, canManageShifts } = useHasPermission()
    if (isManagerOnlyRoute(to.name) && !canManageShifts.value) {
      ray('Org screen for a staff member, sending them to My Account')
        .label('router')
        .color('orange')
      ray().separator()

      return STAFF_LANDING
    }

    if (permission && !can(permission.ability, permission.subject)) {
      ray('Missing permission, cancelling navigation')
        .label('router')
        .color('orange')
      ray().separator()
      
      return '/forbidden'
    }
  }

  ray('Passed before each').label('router').color('green')
  ray().separator()

  return true
}

async function afterEach(): Promise<void> {
  const { endNavigation } = useNavigation()
  endNavigation()
}

function onError(error: unknown) {
  const { endNavigation } = useNavigation()
  endNavigation()

  const eventStore = useEventStore()

  if (error instanceof Error) {
    ray().error(error).label('router').color('red')

    // webpack, then Vite's dynamic import failure as worded by Chromium, Firefox and Safari
    const chunkPattern =
      /Loading chunk ([\w-]+) failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/g
    const chunkMatch = error.message.match(chunkPattern)

    if (chunkMatch) {
      ray('Failed loading chunk', chunkMatch).label('router').color('red')

      const event = new ToastEvent(
        'CliniShift has been updated. Please refresh the page to continue.',
      )
      eventStore.addEvent(event)
    }
  } else {
    ray('Unknown router error', error).label('router').color('red')
  }
}

const router = createRouter({
  routes: routesAuto,
  history: createWebHistory(),
})

router.beforeEach((to, from) =>
  beforeEach(to, from).catch((error: unknown) => {
    // Loading the user's orgs or the org context hit a busy or down API: offer "try again" instead
    // of failing the navigation into a blank page (E2E-R4-02)
    if (isTransientApiError(error)) {
      return toUnavailable(to)
    }

    throw error
  }),
)
router.afterEach(() => afterEach())
router.onError(onError)

if (import.meta.hot) {
  handleHotUpdate(router)
}

export function resolveResumeStep(
  onboardingDraft: OnboardingDraft,
): string | null {
  if (onboardingDraft.completed_at) return null
  const draft = onboardingDraft.draft

  const next = steps.find(
    (s) => !s.isComplete(draft) && !draft.skippedStepIds.includes(s.id),
  )
  if (next) return next.id

  return steps.at(-1)!.id
}

export default router
